export type Treatment = (typeof TREATMENT)[keyof typeof TREATMENT];

export const TREATMENT = {
  STRAIGHT_FIT: "stright-fit",
  MATTED: "matted",
  SHADOWBOX: "shadowbox",
  EXHIBITION_SHADOWBOX: "exhibition-shadowbox",
  MUSEUM_FLOAT: "museum-float",
  MODERN_FLOAT: "modern-float",
} as const;

export const FLOAT_OPTION = {
  MUSEUM: "Museum Float",
  MODERN: "Modern Float",
};
export const FLOAT_OPTIONS = Object.values(FLOAT_OPTION);

export const FRAME_OPTION = {
  JUST_PRINT: "Just the Print",
  ADD_FRAMING: "Add Framing",
};
export const FRAME_OPTIONS = Object.values(FRAME_OPTION);

export const PRINT_OPTION = {
  PRINT_ONLY: "Print Only",
  ADD_MATTING: "Add Matting",
};
export const PRINT_OPTIONS = Object.values(PRINT_OPTION);

export const BORDER_OPTIONS = [0, 0.5, 0.75, 1, 1.5, 2, 2.5, 3];
export const REVEAL_OPTIONS = [0.5, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4];

export const GLAZING_OPTIONS: GlazingOption[] = [
  {
    key: "regular",
    label: "Regular Acrylic",
    options: { opacity: 0.5, type: "Regular Acrylic" },
    url: "/content/acrylic/regular-acrylic.png",
    description: "ACRYLITE® Clear Framing Grade Acrylic",
    limit: [60, 96],
  },
  {
    key: "nonglare",
    label: "Nonglare Acrylic",
    options: { opacity: 0, type: "Nonglare Acrylic" },
    url: "/content/acrylic/nonglare-acrylic.png",
    description: "ACRYLITE® P99 Non-Glare acrylic",
    limit: [48, 96],
  },
  {
    key: "museum",
    label: "Museum Acrylic",
    options: { opacity: 1, type: "Museum Acrylic" },
    url: "/content/acrylic/museum-acrylic.png",
    description: "Tru Vue Optium Museum Acrylic®",
    limit: [52, 96],
  },
];

export const MAT_SIZES = ["4-ply", "8-ply"];
export const MAT_WIDTHS = [1, 1.5, 2, 2.5, 3, 3.5, 4];
export const MAT_COLORS: MatColor[] = [
  {
    id: 1,
    bevelColor: "#ffffff",
    hex: "#ffffff",
    label: "White",
    ply: "both",
    buttonColor: "#ffffff",
    ply4Limit: [60, 96],
    ply8Limit: [48, 96],
  },
  {
    id: 2,
    bevelColor: "#f8f7ec",
    hex: "#f8f7ec",
    label: "Antique White",
    ply: "both",
    buttonColor: "#f8f7ec",
    ply4Limit: [60, 96],
    ply8Limit: [40, 60],
  },
  {
    id: 3,
    bevelColor: "#3b3a3a",
    hex: "#ffffff",
    label: "White with Black Core",
    ply: "4-ply",
    buttonColor: "#ffffff",
    ply4Limit: [40, 60],
  },
  {
    id: 4,
    bevelColor: "#3b3a3a",
    hex: "#363535",
    label: "Black",
    ply: "both",
    buttonColor: "#000000",
    ply4Limit: [48, 96],
    ply8Limit: [40, 60],
  },
  {
    id: 5,
    bevelColor: "#ffffff",
    hex: "#3b3a3a",
    label: "Black with White Core",
    ply: "4-ply",
    buttonColor: "#000000",
    ply4Limit: [40, 60],
  },
  {
    id: 6,
    bevelColor: "#CDD0CC",
    hex: "#CDD0CC",
    label: "Cloud Gray",
    ply: "both",
    buttonColor: "#CDD0CC",
    ply4Limit: [40, 60],
    ply8Limit: [40, 60],
  },
  {
    id: 7,
    bevelColor: "#91846D",
    hex: "#91846D",
    label: "Loam",
    ply: "4-ply",
    buttonColor: "#91846D",
    ply4Limit: [32, 40],
  },
];

export const BACKING_COLORS: BackingColor[] = [
  {
    id: 1,
    buttonColor: "#ffffff",
    hex: "#ffffff",
    label: "White",
  },
  {
    id: 2,
    buttonColor: "#f8f7ec",
    hex: "#f8f7ec",
    label: "Antique White",
  },
  {
    id: 3,
    buttonColor: "#000000",
    hex: "#3b3b3b",
    label: "Black",
  },
];

export const FRAME_TREATMENTS: FrameTreatment[] = [
  {
    key: "straight-fit",
    description:
      "Print is trimmed to full bleed, backed with acid free foam core and framed directly under non-glare acrylic. Available in all frames.",
    fullBleed: true,
    handTorn: false,
    label: "Straight Fit",
    minSize: 0,
    // url: "/content/treatments/straight-fit.png",
  },
  {
    key: "matted",
    description:
      "Add 4-ply or 8-Ply, acid-free archival matting (passe partout) for extra dimension. Matting is in addition to your original image size: e.g. a 16×20 print with a 2″ mat becomes ~20×24 frame.",
    fullBleed: false,
    handTorn: false,
    label: "Matted",
    minSize: 2,
    // url: "/content/treatments/matted.png",
  },
  {
    key: "shadow-box",
    description:
      "The mounted print is set-back 1/2” from the glazing, with customizable borders. This additional border is optimal for signing the print, or simply to let your image “breathe.”.",
    fullBleed: true,
    handTorn: false,
    label: "Shadow Box",
    minSize: 2,
    // url: "/content/treatments/shadow-box.png",
  },
  {
    key: "float-mount",
    description:
      "Framed in our Shadow Box mouldings, the print is raise-mounted and inset for an elegant floating effect, Select Museum Float for additional hand-torn edge, or Modern float for a clean  sleek look. Available in select frames.",
    fullBleed: false,
    handTorn: false,
    label: "Float Mount",
    minSize: 0,
    // url: "/content/treatments/float-mount.png",
  },
];

export const FRAMES: Frame[] = [
  {
    dimensions: { depth: 1.125, face: 0.75 },
    label: "Black Gallery",
    sizes: {
      "straight-fit": 20,
      matted: 20,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "black",
      hex: "#2a2a2a",
      preview: "black",
      side: "black",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Modern Black Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Matte black finish.',
      matted:
        'Matted and Framed Treatment in Modern Black Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Matte black finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/01 Black Gallery.png",
  },
  {
    dimensions: { depth: 1.125, face: 0.75 },
    label: "White Gallery",
    sizes: {
      "straight-fit": 20,
      matted: 20,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "white",
      hex: "#F1F2F1",
      preview: "white",
      side: "white",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Modern White Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Matte white finish.',
      matted:
        'Matted and Framed Treatment in Modern White Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Matte white finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/02 White Gallery frame.png",
  },
  {
    dimensions: { depth: 1.125, face: 0.75 },
    label: "Maple Gallery",
    sizes: {
      "straight-fit": 20,
      matted: 20,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "maple",
      hex: "#faf0e1",
      preview: "maple",
      side: "maple",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Natural Maple Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      matted:
        'Matted and Framed Treatment in Natural Maple Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/03 Maple Gallery frame.png",
  },
  {
    dimensions: { depth: 1.125, face: 0.75 },
    label: "Gold Gallery",
    sizes: {
      "straight-fit": 20,
      matted: 20,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "metallic",
      hex: "#000000",
      preview: "metallic",
      side: "metallic",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Gold Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Metallic gold finish.',
      matted:
        'Matted and Framed Treatment in Gold Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Metallic gold finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/04 Gold Gallery Frame.png",
  },
  {
    dimensions: { depth: 1.125, face: 0.75 },
    label: "Silver Gallery",
    sizes: {
      "straight-fit": 20,
      matted: 20,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "metallic",
      hex: "#000000",
      preview: "metallic",
      side: "metallic",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Silver Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Metallic silver finish.',
      matted:
        'Matted and Framed Treatment in Silver Gallery Style Frame. Solid wood, 3/4" face, 1-1/8" deep from wall. Metallic silver finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/05 Silver Gallery Frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Black",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": 30,
      "shadow-box": 30,
    },
    texture: {
      face: "black",
      hex: "#333333",
      preview: "black",
      side: "black",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float":
        'Float Mount Treatment in Classic Black Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "modern-float":
        'Float Mount Treatment in Classic Black Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "shadow-box":
        'Shadow Box Treatment in Classic Black Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Classic Black Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
    },
    url: "/content/frames/14 black shadow box frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "White",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": 30,
      "shadow-box": 30,
    },
    texture: {
      face: "white",
      hex: "#edf0ee",
      preview: "white",
      side: "white",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float":
        'Float Mount Treatment in Classic White Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "modern-float":
        'Float Mount Treatment in Classic White Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "shadow-box":
        'Shadow Box Treatment in Classic White Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Classic White Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
    },
    url: "/content/frames/15 white shadow box frame.png",
  },
  {
    dimensions: { depth: 2, face: 1 },
    label: "Maple",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": 30,
      "shadow-box": 30,
    },
    texture: {
      face: "maple",
      hex: "#fff0da",
      preview: "maple",
      side: "maple",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float":
        'Float Mount Treatment in Natural Maple Shadow Box Frame. Solid wood, 3/4" face, 2" deep from wall. Matte finish. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "modern-float":
        'Float Mount Treatment in Natural Maple Shadow Box Frame. Solid wood, 3/4" face, 2" deep from wall. Matte finish. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "shadow-box":
        'Shadow Box Treatment in Natural Maple Shadow Box Frame. Solid wood, 3/4" face, 2" deep from wall. Matte finish. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Natural Maple Shadow Box Frame. Solid wood, 3/4" face, 2" deep from wall. Matte finish. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
    },
    url: "/content/frames/16 maple shadow box frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Gold",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": 30,
      "shadow-box": 30,
    },
    texture: {
      face: "metallic",
      hex: "#000000",
      preview: "metallic",
      side: "metallic",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float":
        'Float Mount Treatment in Gold Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic gold finish.',
      "modern-float":
        'Float Mount Treatment in Gold Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic gold finish.',
      "shadow-box":
        'Shadow Box Treatment in Gold Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic gold finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Gold Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic gold finish.',
    },
    url: "/content/frames/17 Gold Shadow Box.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Silver",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": 30,
      "shadow-box": 30,
    },
    texture: {
      face: "metallic",
      hex: "#000000",
      preview: "metallic",
      side: "metallic",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float":
        'Float Mount Treatment in Silver Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic silver finish.',
      "modern-float":
        'Float Mount Treatment in Silver Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic silver finish.',
      "shadow-box":
        'Shadow Box Treatment in Silver Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic silver finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Silver Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic silver finish.',
    },
    url: "/content/frames/18 Silver shadow box frame.png",
  },
  {
    dimensions: { depth: 1.5, face: 0.5 },
    label: "Thin Black",
    sizes: {
      "straight-fit": 9,
      matted: 9,
      "float-mount": 9,
      "shadow-box": 9,
    },
    texture: {
      face: "black",
      hex: "#333333",
      preview: "black",
      side: "black",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Thin Black Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      matted:
        'Matted and Framed Treatment in Thin Black Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "museum-float":
        'Float Mount Treatment in Thin Black Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "modern-float":
        'Float Mount Treatment in Thin Black Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "shadow-box":
        'Shadow Box Treatment in Thin Black Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Thin Black Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
    },
    url: "/content/frames/06 thin black frame.png",
  },
  {
    dimensions: { depth: 1.5, face: 0.5 },
    label: "Thin White",
    sizes: {
      "straight-fit": 9,
      matted: 9,
      "float-mount": 9,
      "shadow-box": 9,
    },
    texture: {
      face: "white",
      hex: "#edf0ee",
      preview: "white",
      side: "white",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Thin White Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      matted:
        'Matted and Framed Treatment in Thin White Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "museum-float":
        'Float Mount Treatment in Thin White Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "modern-float":
        'Float Mount Treatment in Thin White Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "shadow-box":
        'Shadow Box Treatment in Thin White Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Thin White Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Matte finish.',
    },
    url: "/content/frames/07 thin white frame.png",
  },
  {
    dimensions: { depth: 1.5, face: 0.5 },
    label: "Thin Maple",
    sizes: {
      "straight-fit": 9,
      matted: 9,
      "float-mount": 9,
      "shadow-box": 9,
    },
    texture: {
      face: "maple",
      hex: "#fff0da",
      preview: "maple",
      side: "maple",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Thin Maple Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      matted:
        'Matted and Framed Treatment in Thin Maple Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "museum-float":
        'Float Mount Treatment in Thin Maple Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "modern-float":
        'Float Mount Treatment in Thin Maple Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "shadow-box":
        'Shadow Box Treatment in Thin Maple Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Thin Maple Frame. Solid wood, 1/2" face, 1-1/2" deep from wall. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
    },
    url: "/content/frames/08 thin maple frame.png",
  },
  // {
  //   dimensions: { depth: 2, face: 1 },
  //   label: "Dark Walnut Shadow Box",
  //   sizes: {
  //     "straight-fit": null,
  //     matted: null,
  //     "float-mount": null,
  //     "shadow-box": 34,
  //   },
  //   texture: {
  //     face: "weathered",
  //     hex: "",
  //     preview: "weathered",
  //     side: "weathered",
  //   },
  //   description: {
  //     "straight-fit": null,
  //     matted: null,
  //     "museum-float": null,
  //     "modern-float": null,
  //     "shadow-box":
  //       'Shadow Box Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
  //     "exhibition-shadow-box":
  //       'Shadow Box Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
  //   },
  //   url: "/content/frames/09 Dark Walnut.png",
  // },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Weathered Walnut",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": null,
      "shadow-box": 12,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float": null,
      "modern-float": null,
      "shadow-box":
        'Shadow Box Treatment in Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
    },
    url: "/content/frames/10 weathered walnut frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Weathered Gray",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": null,
      "shadow-box": 12,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float": null,
      "modern-float": null,
      "shadow-box":
        'Shadow Box Treatment in Weathered Gray Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Weathered Gray Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
    },
    url: "/content/frames/11 weathered gray frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Weathered Black",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": null,
      "shadow-box": 12,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float": null,
      "modern-float": null,
      "shadow-box":
        'Shadow Box Treatment in Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
    },
    url: "/content/frames/12 weathered black frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Weathered White",
    sizes: {
      "straight-fit": null,
      matted: null,
      "float-mount": null,
      "shadow-box": 12,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit": null,
      matted: null,
      "museum-float": null,
      "modern-float": null,
      "shadow-box":
        'Shadow Box Treatment in Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
    },
    url: "/content/frames/13 weathered white frame.png",
  },
  {
    dimensions: { depth: 1.4375, face: 0.75 },
    label: "Dark Walnut",
    sizes: {
      "straight-fit": 11.11,
      matted: 11.11,
      "float-mount": 11.11,
      "shadow-box": 11.11,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      matted:
        'Matted and Framed Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "shadow-box":
        'Shadow Box Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7 / 16" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Dark Walnut Frame. Rich dark walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7 / 16" deep from wall.',
    },
    url: "/content/frames/09 Dark Walnut.png",
  },
  {
    dimensions: { depth: 2, face: 1 },
    label: "Dark Walnut Shadow Box",
    sizes: {
      "straight-fit": 34,
      matted: 34,
      "float-mount": 34,
      "shadow-box": 34,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Dark Walnut Frame. Dark walnut finish with visible wood grain. Modern, solid wood 1" face, 2" depth from wall.',
      matted:
        'Matted & Framed Treatment in Dark Walnut Frame. Dark walnut finish with visible wood grain. Modern, solid wood 1" face, 2" depth from wall.',
      "museum-float":
        'Float Mount Treatment in Dark Walnut Frame. Dark walnut finish with visible wood grain. Modern, solid wood 1" face, 2" depth from wall.',
      "modern-float":
        'Float Mount Treatment in Dark Walnut Frame. Dark walnut finish with visible wood grain. Modern, solid wood 1" face, 2" depth from wall.',
      "shadow-box":
        'Shadow Box Treatment in Dark Walnut Frame. Dark walnut finish with visible wood grain. Modern, solid wood 1" face, 2" depth from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Dark Walnut Frame. Dark walnut finish with visible wood grain. Modern, solid wood 1" face, 2" depth from wall.',
    },
    url: "/content/frames/25 Dark Walnut.png",
  },
  {
    dimensions: { depth: 1.4375, face: 0.75 },
    label: "Weathered Walnut",
    sizes: {
      "straight-fit": 12,
      matted: 12,
      "float-mount": 12,
      "shadow-box": null,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      matted:
        'Matted and Framed Treatment in Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/10 weathered walnut frame.png",
  },
  {
    dimensions: { depth: 1.4375, face: 0.75 },
    label: "Weathered Gray",
    sizes: {
      "straight-fit": 12,
      matted: 12,
      "float-mount": 12,
      "shadow-box": null,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Weathered Gray Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      matted:
        'Matted and Framed Treatment in Weathered Gray Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Weathered Gray Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Weathered Gray Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/11 weathered gray frame.png",
  },
  {
    dimensions: { depth: 1.4375, face: 0.75 },
    label: "Weathered Black",
    sizes: {
      "straight-fit": 12,
      matted: 12,
      "float-mount": 12,
      "shadow-box": null,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      matted:
        'Matted and Framed Treatment in Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/12 weathered black frame.png",
  },
  {
    dimensions: { depth: 1.4375, face: 0.75 },
    label: "Weathered White",
    sizes: {
      "straight-fit": 12,
      matted: 12,
      "float-mount": 12,
      "shadow-box": null,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      matted:
        'Matted and Framed Treatment in Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 3/4" face, 1-7/16" deep from wall.',
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/13 weathered white frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Black Shadow Box",
    sizes: {
      "straight-fit": 30,
      matted: 30,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "black",
      hex: "#333333",
      preview: "black",
      side: "black",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Classic Black Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      matted:
        'Matted & Framed Fit Treatment in Classic Black Shadow box frame. Modern, solid wood 1" face, 1-7/8" depth from wall. 2" white 4-ply mat by default, customize mat width, depth and color below.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/14 black shadow box frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "White Shadow Box",
    sizes: {
      "straight-fit": 30,
      matted: 30,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "white",
      hex: "#edf0ee",
      preview: "white",
      side: "white",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Classic White Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      matted:
        'Matted and Framed Treatment in Classic White Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Matte finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/15 white shadow box frame.png",
  },
  {
    dimensions: { depth: 2, face: 1 },
    label: "Maple Shadow Box",
    sizes: {
      "straight-fit": 30,
      matted: 30,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "maple",
      hex: "#fff0da",
      preview: "maple",
      side: "maple",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Natural Maple Shadow Box Frame. Solid wood, 3/4" face, 2" deep from wall. Matte finish. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      matted:
        'Matted and Framed Treatment in Natural Maple Shadow Box Frame. Solid wood, 3/4" face, 2" deep from wall. Matte finish. Raw maple, hand-finished with paste wax to maintain the wood\'s natural grain and beauty.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/16 maple shadow box frame.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Gold Shadow Box",
    sizes: {
      "straight-fit": 30,
      matted: 30,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "metallic",
      hex: "#000000",
      preview: "metallic",
      side: "metallic",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Gold Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic gold finish.',
      matted:
        'Matted and Framed Treatment in Gold Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic gold finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/17 Gold Shadow Box.png",
  },
  {
    dimensions: { depth: 1.875, face: 1 },
    label: "Silver Shadow Box",
    sizes: {
      "straight-fit": 30,
      matted: 30,
      "float-mount": null,
      "shadow-box": null,
    },
    texture: {
      face: "metallic",
      hex: "#000000",
      preview: "metallic",
      side: "metallic",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Silver Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic silver finish.',
      matted:
        'Matted and Framed Treatment in Silver Shadow Box Frame. Solid wood, 1" face, 1-7/8" deep from wall. Metallic silver finish.',
      "museum-float": null,
      "modern-float": null,
      "shadow-box": null,
      "exhibition-shadow-box": null,
    },
    url: "/content/frames/18 Silver shadow box frame.png",
  },
  {
    dimensions: { depth: 3, face: 1 },
    label: "Deep Black Shadow Box",
    sizes: {
      "straight-fit": 40,
      matted: 37.5,
      "float-mount": 36,
      "shadow-box": 40,
    },
    texture: {
      face: "black",
      hex: "#333333",
      preview: "black",
      side: "black",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Deep Black Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      matted:
        'Matted and Framed Treatment in Deep Black Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "museum-float":
        'Float Mount Treatment in Deep Black Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "modern-float":
        'Float Mount Treatment in Deep Black Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "shadow-box":
        'Shadow Box Treatment in Deep Black Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Deep Black Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
    },
    url: "/content/frames/19 deep black shadow box frame.png",
  },
  {
    dimensions: { depth: 3, face: 1 },
    label: "Deep White Shadow Box",
    sizes: {
      "straight-fit": 40,
      matted: 37.5,
      "float-mount": 36,
      "shadow-box": 40,
    },
    texture: {
      face: "white",
      hex: "#edf0ee",
      preview: "white",
      side: "white",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Deep White Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      matted:
        'Matted and Framed Treatment in Deep White Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "museum-float":
        'Float Mount Treatment in Deep White Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "modern-float":
        'Float Mount Treatment in Deep White Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "shadow-box":
        'Shadow Box Treatment in Deep White Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Deep White Shadow Box Frame. Solid wood, 1" face, 3" deep from wall. Matte finish.',
    },
    url: "/content/frames/20 deep white shadow box frame.png",
  },
  //Deep Weathered
  {
    dimensions: { depth: 2.25, face: 1.25 },
    label: "Deep Weathered Walnut",
    sizes: {
      "straight-fit": 40,
      matted: 37.5,
      "float-mount": 36,
      "shadow-box": 40,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Deep Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      matted:
        'Matted and Framed Treatment in Deep Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Deep Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Deep Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "shadow-box":
        'Shadow Box Treatment in Deep Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Deep Weathered Walnut Frame. Slightly distressed warm walnut finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
    },
    url: "/content/frames/21 Deep weathered walnut frame.png",
  },
  {
    dimensions: { depth: 2.25, face: 1.25 },
    label: "Deep Weathered Gray",
    sizes: {
      "straight-fit": 40,
      matted: 37.5,
      "float-mount": 36,
      "shadow-box": 40,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Deep Gray Walnut Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      matted:
        'Matted and Framed Treatment in Deep Gray Walnut Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Deep Gray Walnut Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Deep Gray Walnut Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "shadow-box":
        'Shadow Box Treatment in Deep Gray Walnut Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Deep Gray Walnut Frame. Slightly distressed gray finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
    },
    url: "/content/frames/22 Deep weathered gray frame.png",
  },
  {
    dimensions: { depth: 2.25, face: 1.25 },
    label: "Deep Weathered Black",
    sizes: {
      "straight-fit": 40,
      matted: 37.5,
      "float-mount": 36,
      "shadow-box": 40,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Deep Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      matted:
        'Matted and Framed Treatment in Deep Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Deep Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Deep Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "shadow-box":
        'Shadow Box Treatment in Deep Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Deep Weathered Black Frame. Slightly distressed rich black finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
    },
    url: "/content/frames/23 Deep weathered black frame.png",
  },
  {
    dimensions: { depth: 2.25, face: 1.25 },
    label: "Deep Weathered White",
    sizes: {
      "straight-fit": 40,
      matted: 37.5,
      "float-mount": 36,
      "shadow-box": 40,
    },
    texture: {
      face: "weathered",
      hex: "",
      preview: "weathered",
      side: "weathered",
    },
    description: {
      "straight-fit":
        'Straight Fit Treatment in Deep Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      matted:
        'Matted and Framed Treatment in Deep Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "museum-float":
        'Float Mount Treatment in Deep Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "modern-float":
        'Float Mount Treatment in Deep Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "shadow-box":
        'Shadow Box Treatment in Deep Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
      "exhibition-shadow-box":
        'Shadow Box Treatment in Deep Weathered White Frame. Slightly distressed finish with visible wood grain. Modern, solid wood, 1-1/4" face, 2-1/4" deep from wall.',
    },
    url: "/content/frames/24 Deep weathered white frame.png",
  },
];

export type TreatmentType =
  | "float-mount"
  | "matted"
  | "shadow-box"
  | "straight-fit";

export type TreatmentExactType =
  | "straight-fit"
  | "matted"
  | "museum-float"
  | "modern-float"
  | "shadow-box"
  | "exhibition-shadow-box";

export type FrameTreatment = {
  key: TreatmentType;
  handTorn: boolean;
  description: string;
  fullBleed: boolean;
  label: string;
  minSize: number;
  // url: string;
};

export type Dimension = { depth: number; face: number };
type Size = Record<TreatmentType, number | null>;

export type Texture = {
  face: string;
  hex: string;
  preview: string;
  side: string;
};

export type Frame = {
  dimensions: Dimension;
  label: string;
  sizes: Size;
  texture: Texture;
  url: string;
  isSelectable?: any;
  description?: Record<TreatmentExactType, string | null>;
};

export type GlazingOption = {
  key: string;
  label: string;
  options: { opacity: number; type: string };
  url: string;
  description: string;
  limit: number[];
};

export type MatColor = {
  id: number;
  bevelColor: string;
  buttonColor: string;
  hex: string;
  label: string;
  ply?: string;
  ply4Limit?: [number, number];
  ply8Limit?: [number, number];
};

export type BackingColor = {
  id: number;
  buttonColor: string;
  hex: string;
  label: string;
};
